import * as Header from '-/Custom/Header';
import { Stack } from 'expo-router';
import type { ReactNode } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import Avatar from '@/components/Custom/Avatar';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import ModalKeyboardStickyView from '@/components/Custom/ModalKeyboardStickyView';
import PersonEditorScrollView from '@/components/Person/PersonEditorScrollView';
import FormContentBoundary from '@/components/UI/FormContentBoundary';
import Select from '@/components/UI/Select';
import Text from '@/components/UI/Text';
import Box from '@/components/Unistyles/Box';
import type { RoleEnum } from '@/gql/scalars';
import formatMembershipRoleLabel from '@/utils/formatMembershipRoleLabel';
import formatRoleLabel from '@/utils/formatRoleLabel';
import type { MemberUpdateFormValues } from './MemberUpdate.types';

type MemberUpdateRoleInputProps = {
  fetching: boolean;
  label: string;
  roles: readonly RoleEnum[];
  testID: string;
};

const memberTypes: ReadonlyArray<{
  label: string;
  value: MemberUpdateFormValues['memberType'];
}> = [
  { label: 'Participant', value: 'participant' },
  { label: 'Non Participant', value: 'non_participant' },
];

function MemberUpdateScreen({
  children,
  headerTitle,
  fetching,
}: {
  children: ReactNode;
  headerTitle: ReactNode;
  fetching: boolean;
}) {
  return (
    <>
      <Stack.Screen
        options={{
          headerRight: undefined,
          headerTitle: () => (
            <Header.TitleContainer>{headerTitle}</Header.TitleContainer>
          ),
          title: 'Edit Member',
        }}
      />
      <FormContentBoundary loading={fetching}>{children}</FormContentBoundary>
    </>
  );
}

function MemberUpdateHeader({
  children,
  testID,
}: {
  children?: ReactNode;
  testID?: string;
}) {
  const { control } = useFormContext<MemberUpdateFormValues>();
  const color = useWatch({ name: 'color', control });
  const avatarId = useWatch({ name: 'avatarId', control });
  const title = useWatch({ name: 'title', control });

  return (
    <Box center space="md" py="lg" width="100%" testID={testID}>
      <Avatar
        id={avatarId || undefined}
        size="3xl"
        color={color || ''}
        name={title || ''}
      />
      <Text size="4xl" bold center>
        {title}
      </Text>
      {children}
    </Box>
  );
}

function MemberUpdateRoleLabel({ isManaged }: { isManaged?: boolean | null }) {
  const { control } = useFormContext<MemberUpdateFormValues>();
  const role = useWatch({ control, name: 'role' });
  const label = formatMembershipRoleLabel({ isManaged, role });

  return label ? (
    <Text numberOfLines={1} size="sm" variant="typographySecondary">
      {label}
    </Text>
  ) : null;
}

function MemberUpdateFields({
  children,
  masthead,
}: {
  children: ReactNode;
  masthead: ReactNode;
}) {
  const { control } = useFormContext<MemberUpdateFormValues>();
  const { title, avatarId, color } = useWatch({ control });
  return (
    <PersonEditorScrollView
      name={title ?? ''}
      avatarId={avatarId}
      color={color}
      masthead={masthead}
      showCompactHeader={false}
    >
      <Box p="sm" space="md">
        {children}
      </Box>
    </PersonEditorScrollView>
  );
}

function MemberUpdateRoleInput({
  fetching,
  label,
  roles,
  testID,
}: MemberUpdateRoleInputProps) {
  const { control, setValue } = useFormContext<MemberUpdateFormValues>();
  const role = useWatch({ control, name: 'role' });

  return (
    <Box space="md">
      <Text size="xl">{label}</Text>
      <Select
        placeholder="Select..."
        key={role || 'select-role'}
        disabled={fetching}
        testID={testID}
        value={role || null}
        onValueChange={(value) => {
          const selectedRole = roles.find(
            (candidateRole) => candidateRole === value,
          );

          if (!selectedRole) {
            return;
          }

          setValue('role', selectedRole, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }}
        options={roles.map((optionRole) => ({
          label: formatRoleLabel(optionRole),
          value: optionRole,
        }))}
      />
    </Box>
  );
}

function MemberUpdateMemberTypeInput({ testID }: { testID: string }) {
  const { control, setValue } = useFormContext<MemberUpdateFormValues>();
  const memberType = useWatch({ control, name: 'memberType' });

  return (
    <Box space="md">
      <Text size="xl">Member Type</Text>
      <Select
        key={memberType}
        options={memberTypes}
        placeholder="Select..."
        testID={testID}
        value={memberType}
        onValueChange={(value) => {
          const selectedMemberType = memberTypes.find(
            (candidate) => candidate.value === value,
          )?.value;

          if (!selectedMemberType) {
            return;
          }

          setValue('memberType', selectedMemberType, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }}
      />
    </Box>
  );
}

function MemberUpdateSave({
  disabled,
  onSave,
}: {
  disabled: boolean;
  onSave(): void | Promise<void>;
}) {
  return (
    <ModalKeyboardStickyView>
      <FormSaveButton
        info={{ text: 'Save' }}
        isDisabled={disabled}
        onPress={onSave}
      />
    </ModalKeyboardStickyView>
  );
}

export const MemberUpdate = {
  Fields: MemberUpdateFields,
  Header: MemberUpdateHeader,
  MemberTypeInput: MemberUpdateMemberTypeInput,
  RoleInput: MemberUpdateRoleInput,
  RoleLabel: MemberUpdateRoleLabel,
  Save: MemberUpdateSave,
  Screen: MemberUpdateScreen,
};
